import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Lock, Save, RefreshCw, History, AlertTriangle, DollarSign, Coins } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// NOTE: Admin token stored in sessionStorage (cleared on tab close) instead of localStorage
// to limit XSS exposure window. This page is admin-only and the token grants full pricing override
// capability — DO NOT use this app on shared computers.
const TOKEN_KEY = 'b4p_admin_token';

export default function AdminPricingPage() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || '');
    const [tokenInput, setTokenInput] = useState('');
    const [state, setState] = useState(null);
    const [pegs, setPegs] = useState(null);
    const [priceInput, setPriceInput] = useState('');
    const [activeInput, setActiveInput] = useState(true);
    const [loading, setLoading] = useState(false);

    const authHeaders = useCallback(() => token ? { 'X-Admin-Token': token } : {}, [token]);

    const loadState = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true);
            const [r, p] = await Promise.all([
                fetch(`${API_URL}/api/pricing/admin-state`, { headers: authHeaders() }),
                fetch(`${API_URL}/api/pricing/preview-rewards`),
            ]);
            if (r.status === 403) {
                toast.error('Token admin invalide');
                setToken(''); sessionStorage.removeItem(TOKEN_KEY);
                return;
            }
            if (!r.ok) throw new Error('admin-state ' + r.status);
            const data = await r.json();
            setState(data);
            setPegs(await p.json());
            setPriceInput(String(data?.override?.price ?? data?.fallback_env_usd ?? ''));
            setActiveInput(Boolean(data?.override?.active));
        } catch (e) {
            toast.error('Erreur chargement: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, [token, authHeaders]);

    useEffect(() => { loadState(); }, [loadState]);

    const submitToken = () => {
        const t = tokenInput.trim();
        if (!t) return;
        sessionStorage.setItem(TOKEN_KEY, t);
        setToken(t);
    };

    const submitOverride = async () => {
        const price = parseFloat(priceInput);
        if (!price || price <= 0) {
            toast.error('Prix invalide');
            return;
        }
        try {
            const r = await fetch(`${API_URL}/api/pricing/admin-override`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ price_usd: price, active: activeInput }),
            });
            if (!r.ok) {
                const e = await r.json().catch(() => ({}));
                toast.error(e.detail || `Erreur ${r.status}`);
                return;
            }
            toast.success(`Prix mis à jour: $${price}/B4P (${activeInput ? 'actif' : 'désactivé'})`);
            await loadState();
        } catch (e) {
            toast.error('Erreur: ' + e.message);
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        setState(null);
    };

    if (!token) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="admin-pricing-auth">
                <Card className="w-full max-w-md bg-white/5 border-white/10">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-white">
                            <Lock className="w-5 h-5 text-[#FFD700]" />
                            Pricing Admin
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <p className="text-sm text-white/60">
                            Entrez le token admin (variable <code className="text-[#00FF66]">ADMIN_TOKEN</code> du backend) pour accéder au tableau de bord pricing.
                        </p>
                        <Input
                            type="password"
                            value={tokenInput}
                            onChange={(e) => setTokenInput(e.target.value)}
                            placeholder="ADMIN_TOKEN"
                            className="bg-black/40 border-white/10 text-white"
                            data-testid="admin-token-input"
                            onKeyDown={(e) => e.key === 'Enter' && submitToken()}
                        />
                        <Button onClick={submitToken} className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80" data-testid="admin-token-submit">
                            Authentifier
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-pricing-page">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div>
                    <h1 className="font-serif-luxury text-4xl md:text-5xl text-white tracking-tight divider-gold flex items-center gap-3">
                        <DollarSign className="w-7 h-7 text-[#FFD700]" />
                        <span className="text-gold-gradient">Pricing</span> <span className="font-serif-luxury-italic text-white/80">Admin</span>
                    </h1>
                    <p className="text-sm text-white/50 mt-3">
                        Hybrid USD-Pegged Reward Control
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={loadState} disabled={loading} data-testid="admin-refresh-btn"
                        className="border-white/20 text-white">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button variant="outline" onClick={logout} data-testid="admin-logout-btn"
                        className="border-white/20 text-white">
                        <Lock className="w-4 h-4 mr-2" />
                        Logout
                    </Button>
                </div>
            </div>

            {/* Current State */}
            <div className="grid md:grid-cols-3 gap-4 mb-8">
                <Card className="bg-white/5 border-white/10" data-testid="admin-current-price">
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-white/50 mb-1">Prix actuel</div>
                        <div className="font-mono text-3xl font-bold text-[#FFD700]">
                            ${state ? Number(state.current.price).toFixed(state.current.price < 0.01 ? 6 : 4) : '...'}
                        </div>
                        <Badge className="mt-2 bg-white/10 text-white/70 border-0">
                            src: {state?.current?.source || '...'}
                        </Badge>
                    </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10" data-testid="admin-override-state">
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-white/50 mb-1">Override</div>
                        <div className="font-mono text-2xl font-bold text-white">
                            ${state?.override?.price != null ? state.override.price : '—'}
                        </div>
                        <Badge className={`mt-2 border-0 ${state?.override?.active ? 'bg-[#00FF66]/15 text-[#00FF66]' : 'bg-white/10 text-white/50'}`}>
                            {state?.override?.active ? 'ACTIF' : 'INACTIF'}
                        </Badge>
                    </CardContent>
                </Card>
                <Card className="bg-white/5 border-white/10" data-testid="admin-fallback-env">
                    <CardContent className="p-5">
                        <div className="text-xs uppercase text-white/50 mb-1">Fallback (.env)</div>
                        <div className="font-mono text-2xl font-bold text-white/70">
                            ${state?.fallback_env_usd ?? '—'}
                        </div>
                        <div className="text-[10px] text-white/40 mt-2">B4P_USD_PRICE_FALLBACK</div>
                    </CardContent>
                </Card>
            </div>

            {/* Override Form */}
            <Card className="bg-white/5 border-white/10 mb-8">
                <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Save className="w-5 h-5 text-[#00FF66]" />
                        Définir l'override
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-xs text-yellow-300/80 bg-yellow-300/5 border border-yellow-300/20 rounded-md p-3">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        Le prix override remplace le fallback .env et toute source CoinGecko jusqu'à désactivation. Cache 60s invalidé immédiatement.
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-white/50 mb-1 block">Prix USD par 1 B4P</label>
                            <Input
                                type="number"
                                step="0.0001"
                                value={priceInput}
                                onChange={(e) => setPriceInput(e.target.value)}
                                className="bg-black/40 border-white/10 text-white font-mono"
                                data-testid="admin-price-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-white/50 mb-1 block">État</label>
                            <select
                                value={activeInput ? '1' : '0'}
                                onChange={(e) => setActiveInput(e.target.value === '1')}
                                className="w-full h-10 bg-black/40 border border-white/10 rounded-md text-white px-3"
                                data-testid="admin-active-select"
                            >
                                <option value="1">Activer</option>
                                <option value="0">Désactiver</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <Button onClick={submitOverride}
                                className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                data-testid="admin-save-override-btn">
                                Sauvegarder
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Live Rewards Preview */}
            <Card className="bg-white/5 border-white/10 mb-8">
                <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                        <Coins className="w-5 h-5 text-[#FFD700]" />
                        Récompenses live (à ce prix)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid md:grid-cols-2 gap-2" data-testid="admin-pegs-grid">
                        {pegs?.pegs?.map((p) => (
                            <div key={p.id} className="flex items-center justify-between bg-black/30 border border-white/5 rounded-md px-3 py-2.5"
                                data-testid={`peg-${p.id}`}>
                                <div>
                                    <div className="text-sm text-white">{p.label}</div>
                                    <div className="text-[10px] text-white/40">peg ${p.usd} · floor {p.min_b4p} / ceiling {p.max_b4p} B4P</div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-[#00FF66] font-bold">{p.b4p_amount} B4P</div>
                                    {p.clamped && <div className="text-[10px] text-yellow-400">clamped</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* History */}
            <Card className="bg-white/5 border-white/10">
                <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                        <History className="w-5 h-5 text-white/60" />
                        Historique des overrides ({state?.history?.length || 0})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {(!state?.history || state.history.length === 0) && (
                        <div className="text-sm text-white/40 italic">Aucun override enregistré.</div>
                    )}
                    <div className="space-y-1.5" data-testid="admin-history">
                        {state?.history?.map((h, i) => (
                            <div key={i} className="flex items-center justify-between bg-black/30 border border-white/5 rounded-md px-3 py-2 text-sm">
                                <span className="font-mono text-white">${h.price}</span>
                                <Badge className={`border-0 ${h.active ? 'bg-[#00FF66]/15 text-[#00FF66]' : 'bg-white/10 text-white/50'}`}>
                                    {h.active ? 'Activé' : 'Désactivé'}
                                </Badge>
                                <span className="text-xs text-white/40 font-mono">
                                    {new Date(h.created_at * 1000).toLocaleString()}
                                </span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
